import { addActor, after, between, budget, rand, spawn } from '../engine';
import { burst, ease, explode, glints, launch, light, rain, shock, wash, type Stage } from '../kit';
import {
  bubble,
  butterfly,
  candy,
  fish,
  gear,
  glow,
  glowSprite,
  koi,
  leaf,
  lollipop,
  lotus,
  nut,
  petal,
  planet,
  puff,
  shard,
  spark,
  sprinkle,
  stamp,
  twinkle,
} from '../shapes';
import { serpent } from './pieces';
import { TAU, UP, type Preset } from './types';

/* ===================================== Garden: petals, leaves, butterflies, a sunflower ===================================== */

const PETALS = ['#F7A8C4', '#FFF1F5', '#FFD166', '#FF9F43', '#F27BA3'] as const;
const LEAVES = ['#7BC96F', '#4E9F5A', '#A6DB77'] as const;
const WINGS = ['#FFB347', '#F27BA3', '#7FD6FF', '#FFD166'] as const;

function butterflies(s: Stage, n: number, delayMs = 0): void {
  for (let i = 0; i < budget(n); i++) {
    const left = i % 2 === 0;
    spawn({
      x: left ? between(-30, s.W * 0.2) : between(s.W * 0.8, s.W + 30),
      y: s.H + between(10, 40),
      vx: (left ? 1 : -1) * between(60, 150),
      vy: -between(160, 240),
      delay: delayMs / 1000 + i * 0.12,
      draw: butterfly,
      col: WINGS[i % WINGS.length],
      col2: WINGS[(i + 1) % WINGS.length],
      size: between(15, 21),
      life: between(2.4, 3),
      swayY: between(40, 70),
      sway: between(30, 60),
      swayF: between(2.5, 4),
      face: true,
      fadeOut: 0.8,
    });
  }
}

function sunflower(): void {
  let bloomed = false;
  const seeds = Array.from({ length: 89 }, (_, i) => ({ a: i * 2.39996, r: Math.sqrt(i / 89) }));
  addActor({
    dur: 3.3,
    draw(g, k, f) {
      const cx = f.W / 2;
      const cy = f.H * 0.44;
      const R = Math.min(f.W, f.H) * 0.22;
      const a = ease.env(k, 0.08, 0.8);
      const grow = ease.back(ease.span(k, 0, 0.28));
      const spin = f.t * 0.22;
      const sp = glowSprite('#FFD166');
      if (sp) {
        g.globalCompositeOperation = 'lighter';
        g.globalAlpha = 0.55 * a;
        const r = R * 3 * grow;
        g.drawImage(sp, cx - r, cy - r, r * 2, r * 2);
        g.globalCompositeOperation = 'source-over';
      }
      g.globalAlpha = a;
      // two rings of petals unfolding one after another
      for (const [ring, n, len, col, col2] of [
        [0, 21, 0.62, '#F2A81D', '#FFE08A'],
        [1, 21, 0.56, '#FFC93C', '#FFF1B8'],
      ] as const) {
        for (let i = 0; i < n; i++) {
          const open = ease.back(ease.span(k, 0.06 + i * 0.008 + ring * 0.05, 0.3 + i * 0.008 + ring * 0.05));
          if (open <= 0) continue;
          const an = (i / n) * TAU + spin + (ring * Math.PI) / n;
          const d = R * (0.5 + len * 0.5 * open);
          stamp(
            g,
            petal,
            cx + Math.cos(an) * d,
            cy + Math.sin(an) * d,
            R * len * 0.62 * open * grow,
            an + Math.PI / 2,
            col,
            col2,
            f.dpr,
          );
        }
      }
      // the seed disc (golden-angle spiral)
      g.globalAlpha = a;
      g.fillStyle = '#5A3418';
      g.beginPath();
      g.arc(cx, cy, Math.max(0, R * 0.52 * grow), 0, TAU);
      g.fill();
      g.fillStyle = '#8A5523';
      for (const sd of seeds) {
        const an = sd.a + spin;
        const r = sd.r * R * 0.46 * grow;
        g.beginPath();
        g.arc(cx + Math.cos(an) * r, cy + Math.sin(an) * r, Math.max(0.4, R * 0.028 * grow), 0, TAU);
        g.fill();
      }
      g.fillStyle = '#FFFFFF';
      g.globalAlpha = a * 0.18;
      g.beginPath();
      g.arc(cx - R * 0.16, cy - R * 0.18, Math.max(0, R * 0.2 * grow), 0, TAU);
      g.fill();
      if (!bloomed && k > 0.3) {
        bloomed = true;
        const st: Stage = { x: cx, y: cy, W: f.W, H: f.H, pal: PETALS, light: '#FFF6D8', ink: '#FFFFFF' };
        burst(st, cx, cy, {
          n: 70,
          r: R * 0.9,
          speed: [160, 420],
          draw: petal,
          cols: ['#FFC93C', '#F2A81D', '#FFE08A', ...PETALS],
          col2: '#FFFFFF',
          size: [6, 10],
          life: [1.4, 2],
          gravity: 160,
          drag: 0.45,
          flip: [4, 8],
          vr: [-3, 3],
        });
        glints(st, cx, cy, 24, R);
      }
    },
  });
}

export const garden: Preset = {
  pal: PETALS,
  light: '#FFF6D8',
  win(s) {
    light(s, s.x, s.y, 60, '#FFD166', 0.5, 0.55);
    burst(s, s.x, s.y, {
      n: 26,
      speed: [120, 320],
      angle: UP,
      spread: Math.PI * 1.6,
      draw: petal,
      col2: '#FFFFFF',
      size: [5, 8],
      life: [0.7, 0.95],
      gravity: 420,
      drag: 0.4,
      flip: [6, 10],
      vr: [-4, 4],
    });
    burst(s, s.x, s.y, {
      n: 8,
      speed: [100, 260],
      angle: UP,
      spread: Math.PI * 1.4,
      draw: leaf,
      cols: LEAVES,
      col2: '#E8F7D8',
      size: [6, 9],
      life: [0.7, 0.9],
      gravity: 380,
      drag: 0.4,
      flip: [4, 7],
      vr: [-3, 3],
    });
    glints(s, s.x, s.y, 8, 30);
  },
  big(s) {
    rain(s, {
      n: 190,
      over: 1300,
      vy: [90, 170],
      vx: [-20, 40],
      draw: petal,
      col2: '#FFFFFF',
      size: [7, 12],
      life: [2, 2.6],
      sway: [20, 50],
      swayF: [2, 4],
      flip: [3, 7],
      vr: [-2, 2],
      gravity: 30,
      fadeOut: 0.8,
    });
    rain(s, {
      n: 60,
      over: 1200,
      vy: [80, 160],
      vx: [0, 50],
      draw: leaf,
      cols: LEAVES,
      col2: '#E8F7D8',
      size: [9, 14],
      life: [2, 2.6],
      sway: [30, 60],
      flip: [2, 5],
      vr: [-2, 2],
      gravity: 30,
    });
    butterflies(s, 6);
    burst(s, s.x, s.y, {
      n: 40,
      speed: [160, 420],
      angle: UP,
      spread: Math.PI * 1.6,
      draw: petal,
      col2: '#FFFFFF',
      size: [6, 9],
      life: [1, 1.4],
      gravity: 380,
      drag: 0.45,
      flip: [5, 9],
      vr: [-4, 4],
    });
    light(s, s.x, s.y, 90, '#FFD166', 0.7, 0.6);
  },
  mega(s) {
    garden.big(s);
    sunflower();
    butterflies(s, 8, 900);
    wash(s.W / 2, s.H * 0.44, { col: '#FFD166', r: Math.max(s.W, s.H) * 0.6, dur: 2.6, alpha: 0.35 });
  },
};

/* ===================================== Deep: bubbles, a school of fish, the whale wave ===================================== */

const BUBBLES = ['#BFEFFF', '#8FE3FF', '#E8F6FF', '#7FD6FF'] as const;
const FISH = ['#FF8A3D', '#FFD166', '#4FC3F7', '#F07AA8', '#2EE6C9'] as const;
const WHALE =
  'M1 .05C.95-.3.55-.42.1-.4C-.35-.38-.7-.2-.92-.02L-1.18-.3C-1.22-.12-1.2.1-1.16.26L-.92.06C-.7.26-.3.4.15.38C.6.36.95.3 1 .05Z';
let whalePath: Path2D | null | undefined;

function school(s: Stage, n: number, fromLeft: boolean, band: readonly [number, number], delay = 0): void {
  const y0 = s.H * between(band[0], band[1]);
  for (let i = 0; i < budget(n); i++) {
    spawn({
      x: fromLeft ? -between(20, 160) : s.W + between(20, 160),
      y: y0 + between(-50, 50),
      vx: (fromLeft ? 1 : -1) * between(280, 360),
      vy: between(-12, 12),
      delay: delay + i * 0.04,
      draw: fish,
      col: FISH[i % FISH.length],
      col2: '#FFFFFF',
      size: between(9, 13),
      life: 3.6,
      swayY: between(25, 45),
      swayF: 3.5,
      face: true,
    });
  }
}

function whale(): void {
  let spouted = false;
  let acc = 0;
  addActor({
    dur: 3.4,
    draw(g, k, f) {
      const L = Math.min(f.W * 0.27, 360);
      const e = ease.inOut(k);
      const x = -L * 1.4 + (f.W + L * 2.8) * e;
      const y = f.H * 0.68 + Math.sin(k * TAU) * f.H * 0.04;
      const tilt = Math.cos(k * TAU) * 0.08;
      // the light wave following the whale
      g.globalCompositeOperation = 'lighter';
      for (const [w, al] of [
        [26, 0.07],
        [10, 0.14],
        [3, 0.55],
      ] as const) {
        g.globalAlpha = al * ease.env(k, 0.1, 0.8);
        g.strokeStyle = '#8FE3FF';
        g.lineWidth = w;
        g.beginPath();
        for (let px = -20; px <= f.W + 20; px += 16) {
          const wy =
            y + L * 0.5 + Math.sin(px * 0.012 - f.t * 3) * 24 * Math.max(0, 1 - Math.abs(px - x) / (L * 3));
          if (px === -20) g.moveTo(px, wy);
          else g.lineTo(px, wy);
        }
        g.stroke();
      }
      // god rays from the surface
      for (let i = 0; i < 5; i++) {
        const rx = (f.W * (i + 0.5)) / 5 + Math.sin(f.t * 0.7 + i) * 40;
        g.globalAlpha = 0.06 * ease.env(k, 0.15, 0.7);
        g.fillStyle = '#BFEFFF';
        g.beginPath();
        g.moveTo(rx - 30, 0);
        g.lineTo(rx + 30, 0);
        g.lineTo(rx + 140, f.H);
        g.lineTo(rx - 10, f.H);
        g.closePath();
        g.fill();
      }
      // the whale: a luminous silhouette
      if (whalePath === undefined) whalePath = typeof Path2D === 'undefined' ? null : new Path2D(WHALE);
      g.save();
      g.translate(x, y);
      g.rotate(tilt);
      g.scale(L, L);
      const sp = glowSprite('#4FC3F7');
      if (sp) {
        g.globalAlpha = 0.45;
        g.drawImage(sp, -1.6, -1, 3.2, 2);
      }
      g.globalAlpha = 0.6;
      const gr =
        typeof g.createLinearGradient === 'function' ? g.createLinearGradient(0, -0.4, 0, 0.4) : null;
      if (gr && typeof gr.addColorStop === 'function') {
        gr.addColorStop(0, '#1F5FA8');
        gr.addColorStop(0.55, '#3FA0E0');
        gr.addColorStop(1, '#BFEFFF');
        g.fillStyle = gr;
      } else g.fillStyle = '#4FA9E8';
      if (whalePath) g.fill(whalePath);
      g.strokeStyle = '#E8F6FF';
      g.globalAlpha = 0.5;
      g.lineWidth = 0.012;
      g.beginPath();
      for (let i = 0; i < 6; i++) {
        g.moveTo(0.75 - i * 0.12, 0.2);
        g.lineTo(0.55 - i * 0.12, 0.32);
      }
      g.stroke();
      g.globalCompositeOperation = 'source-over';
      g.fillStyle = '#0B1424';
      g.globalAlpha = 0.8;
      g.beginPath();
      g.arc(0.72, -0.05, 0.03, 0, TAU);
      g.fill();
      g.globalCompositeOperation = 'lighter';
      g.globalAlpha = 0.5;
      g.fillStyle = '#3FA0E0';
      g.beginPath();
      g.moveTo(0.35, 0.3);
      g.bezierCurveTo(0.25, 0.6, 0.05, 0.7, -0.05, 0.62);
      g.bezierCurveTo(0.05, 0.48, 0.15, 0.38, 0.2, 0.32);
      g.fill();
      g.restore();
      // bubbles from the mouth, a spout of light at the middle
      if (k < 0.9) {
        acc += f.dt * 26;
        while (acc >= 1) {
          acc -= 1;
          spawn({
            x: x + L * 0.95,
            y: y + L * 0.1,
            vx: between(-40, 40),
            vy: -between(80, 180),
            draw: bubble,
            col: '#BFEFFF',
            size: between(3, 8),
            life: between(1, 1.6),
            gravity: -90,
            sway: 30,
            swayF: 6,
          });
        }
      }
      if (!spouted && k > 0.45) {
        spouted = true;
        const st: Stage = { x, y, W: f.W, H: f.H, pal: BUBBLES, light: '#FFFFFF', ink: '#FFFFFF' };
        burst(st, x + L * 0.6, y - L * 0.38, {
          n: 60,
          speed: [200, 480],
          angle: UP,
          spread: 0.9,
          draw: [bubble, glow],
          cols: BUBBLES,
          size: [3, 9],
          life: [1, 1.5],
          gravity: 300,
          drag: 0.6,
          add: false,
        });
        glints(st, x + L * 0.6, y - L * 0.5, 16, 40, ['#FFFFFF', '#BFEFFF']);
      }
    },
  });
}

export const deep: Preset = {
  pal: BUBBLES,
  light: '#FFFFFF',
  win(s) {
    light(s, s.x, s.y, 60, '#4FC3F7', 0.6, 0.5);
    burst(s, s.x, s.y, {
      n: 28,
      r: 24,
      speed: [30, 120],
      angle: UP,
      spread: Math.PI,
      draw: bubble,
      size: [3, 8],
      life: [0.7, 1],
      gravity: -280,
      sway: [20, 40],
      swayF: [5, 8],
      fadeIn: 0.1,
    });
    glints(s, s.x, s.y, 6, 30, ['#FFFFFF', '#BFEFFF']);
  },
  big(s) {
    rain(s, {
      n: 170,
      over: 1400,
      from: 'bottom',
      vy: [-170, -320],
      draw: bubble,
      size: [3, 11],
      life: [2.2, 3],
      gravity: -80,
      sway: [20, 50],
      swayF: [4, 7],
    });
    school(s, 14, true, [0.3, 0.45]);
    school(s, 9, false, [0.6, 0.72], 0.35);
    burst(s, s.x, s.y, {
      n: 40,
      r: 30,
      speed: [40, 160],
      angle: UP,
      spread: Math.PI,
      draw: bubble,
      size: [4, 10],
      life: [1, 1.5],
      gravity: -260,
      sway: [20, 40],
      swayF: [5, 8],
    });
    light(s, s.x, s.y, 90, '#4FC3F7', 0.8, 0.5);
  },
  mega(s) {
    deep.big(s);
    whale();
    wash(s.W / 2, s.H * 0.5, { col: '#4FC3F7', r: Math.max(s.W, s.H) * 0.6, dur: 3, alpha: 0.3 });
  },
};

/* ===================================== Spaceport: star warp, planet rings, a supernova ===================================== */

const SPACE = ['#A78BFA', '#5B7CFA', '#4FC3F7', '#F07AA8', '#FF8A3D'] as const;
const STARLIGHT = ['#FFFFFF', '#BFD4FF', '#A78BFA', '#4FC3F7'] as const;

function warp(s: Stage, n: number, over: number): void {
  const cx = s.W / 2;
  const cy = s.H / 2;
  for (let i = 0; i < budget(n); i++) {
    const a = rand() * TAU;
    const r0 = between(10, 140);
    const v = between(50, 130);
    spawn({
      x: cx + Math.cos(a) * r0,
      y: cy + Math.sin(a) * r0,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v,
      delay: (rand() * over) / 1000,
      drag: 4.5,
      draw: spark,
      col: STARLIGHT[i % STARLIGHT.length],
      size: between(1.2, 2.4),
      life: between(1, 1.4),
      face: true,
      add: true,
      fadeIn: 0.25,
      fadeOut: 0.75,
    });
  }
}

function supernova(s: Stage): void {
  let blown = false;
  addActor({
    dur: 3.6,
    draw(g, k, f) {
      const cx = f.W / 2;
      const cy = f.H * 0.45;
      const M = Math.max(f.W, f.H);
      const core = glowSprite('#FFFFFF');
      const neb1 = glowSprite('#A78BFA');
      const neb2 = glowSprite('#F07AA8');
      g.globalCompositeOperation = 'lighter';
      if (k < 0.18) {
        // gathering: a pulsing star
        const r = 30 + 60 * (k / 0.18) + Math.sin(f.t * 40) * 6;
        if (core) {
          g.globalAlpha = 0.9;
          g.drawImage(core, cx - r, cy - r, r * 2, r * 2);
        }
      } else {
        if (!blown) {
          blown = true;
          const st: Stage = { ...s, x: cx, y: cy, W: f.W, H: f.H };
          for (const [i, col] of ['#FFFFFF', '#A78BFA', '#4FC3F7', '#FF8A3D'].entries())
            shockWave(cx, cy, col, M * (0.5 + i * 0.12), 1.4 + i * 0.2, i * 0.08);
          burst(st, cx, cy, {
            n: 170,
            speed: [200, 720],
            draw: spark,
            cols: STARLIGHT,
            size: [1.4, 2.6],
            life: [1, 1.6],
            drag: 0.4,
            face: true,
            add: true,
            fadeOut: 0.55,
          });
          burst(st, cx, cy, {
            n: 50,
            speed: [60, 300],
            draw: [twinkle, glow],
            cols: [...STARLIGHT, '#FFD166'],
            size: [3, 8],
            life: [1.4, 2.2],
            drag: 0.5,
            vr: [-3, 3],
            add: true,
          });
        }
        const q = (k - 0.18) / 0.82;
        const fade = 1 - q;
        if (neb1 && neb2) {
          const r1 = M * (0.25 + 0.4 * ease.out(q));
          g.globalAlpha = 0.35 * fade;
          g.drawImage(neb1, cx - r1, cy - r1 * 0.8, r1 * 2, r1 * 1.6);
          g.globalAlpha = 0.25 * fade;
          g.drawImage(neb2, cx - r1 * 0.7 + 40, cy - r1 * 0.6 - 20, r1 * 1.4, r1 * 1.2);
        }
        if (core) {
          const r = 70 * (1 - q * 0.6) + Math.sin(f.t * 9) * 5;
          g.globalAlpha = 0.95 * fade + 0.05;
          g.drawImage(core, cx - r, cy - r, r * 2, r * 2);
        }
        if (q < 0.08) {
          // the flash
          g.globalAlpha = 0.5 * (1 - q / 0.08);
          g.fillStyle = '#FFFFFF';
          g.fillRect(0, 0, f.W, f.H);
        }
      }
    },
  });
}

function shockWave(x: number, y: number, col: string, r: number, dur: number, delay: number): void {
  shock(x, y, { col, r, dur, width: 6, delay });
}

export const spaceport: Preset = {
  pal: SPACE,
  light: '#FFFFFF',
  win(s) {
    light(s, s.x, s.y, 60, '#A78BFA', 0.5, 0.6);
    burst(s, s.x, s.y, {
      n: 28,
      speed: [180, 400],
      draw: spark,
      cols: STARLIGHT,
      size: [1.4, 2.2],
      life: [0.5, 0.8],
      drag: 0.25,
      face: true,
      add: true,
    });
    burst(s, s.x, s.y, {
      n: 12,
      speed: [40, 160],
      draw: twinkle,
      cols: STARLIGHT,
      size: [3, 6],
      life: [0.6, 0.9],
      drag: 0.4,
      vr: [-3, 3],
      add: true,
    });
  },
  big(s) {
    warp(s, 230, 1300);
    for (let i = 0; i < budget(6); i++) {
      const a = rand() * TAU;
      const v = between(60, 140);
      spawn({
        x: s.W / 2,
        y: s.H / 2,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        delay: i * 0.15,
        draw: planet,
        col: SPACE[i % SPACE.length],
        col2: '#E4EAF4',
        size: between(8, 12),
        grow: 0.9,
        life: 2.2,
        vr: between(-0.5, 0.5),
        fadeIn: 0.2,
      });
    }
    burst(s, s.x, s.y, {
      n: 30,
      speed: [160, 420],
      draw: spark,
      cols: STARLIGHT,
      size: [1.4, 2.4],
      life: [0.6, 1],
      drag: 0.3,
      face: true,
      add: true,
    });
    light(s, s.W / 2, s.H / 2, 160, '#5B7CFA', 1.6, 0.35);
  },
  mega(s) {
    warp(s, 160, 900);
    supernova(s);
    for (let i = 0; i < budget(5); i++) {
      const a = (i / 5) * TAU;
      spawn({
        x: s.W / 2,
        y: s.H * 0.45,
        vx: Math.cos(a) * 120,
        vy: Math.sin(a) * 120,
        delay: 0.7 + i * 0.1,
        draw: planet,
        col: SPACE[i % SPACE.length],
        col2: '#E4EAF4',
        size: 10,
        grow: 1.1,
        life: 2.4,
        vr: 0.4,
        fadeIn: 0.2,
      });
    }
  },
};

/* ===================================== Sweet Shop: sprinkles, candies, the candy jar bursts ===================================== */

const SWEET = ['#F07AA8', '#7BE0C3', '#FFE07A', '#B79CFF', '#FF8A7A', '#8FD3FF'] as const;
const SWEETS = [candy, lollipop] as const;

function candyJar(s: Stage): void {
  const beans = Array.from({ length: 26 }, (_, i) => ({
    x: (i % 5) / 4 - 0.5,
    y: Math.floor(i / 5) / 5,
    c: SWEET[i % SWEET.length] ?? '#F07AA8',
  }));
  addActor({
    dur: 1.25,
    draw(g, k, f) {
      const cx = f.W / 2;
      const cy = f.H * 0.46;
      const w = Math.min(f.W, f.H) * 0.13;
      const h = w * 1.25;
      const pop = ease.back(ease.span(k, 0, 0.3));
      const shakeK = ease.span(k, 0.35, 1);
      const sx = Math.sin(f.t * 70) * 7 * shakeK;
      const sc = pop * (1 + 0.15 * shakeK);
      g.save();
      g.translate(cx + sx, cy);
      g.scale(sc, sc);
      g.rotate(Math.sin(f.t * 55) * 0.05 * shakeK);
      // candies inside
      for (const b of beans) {
        g.fillStyle = b.c;
        g.beginPath();
        g.arc(b.x * w * 1.5, h * 0.75 - b.y * h * 1.6, w * 0.16, 0, TAU);
        g.fill();
      }
      // glass
      g.globalAlpha = 0.28;
      g.fillStyle = '#CFF2FF';
      g.beginPath();
      g.roundRect?.(-w, -h, w * 2, h * 2, w * 0.35);
      if (!g.roundRect) g.rect(-w, -h, w * 2, h * 2);
      g.fill();
      g.globalAlpha = 0.9;
      g.strokeStyle = '#E8F6FF';
      g.lineWidth = 3;
      g.stroke();
      g.globalAlpha = 0.55;
      g.fillStyle = '#FFFFFF';
      g.fillRect(-w * 0.72, -h * 0.75, w * 0.14, h * 1.3);
      // lid
      g.globalAlpha = 1;
      g.fillStyle = '#F07AA8';
      g.beginPath();
      g.roundRect?.(-w * 1.08, -h - w * 0.32, w * 2.16, w * 0.4, w * 0.12);
      if (!g.roundRect) g.rect(-w * 1.08, -h - w * 0.32, w * 2.16, w * 0.4);
      g.fill();
      g.restore();
    },
  });
  after(1200, () => {
    const cx = s.W / 2;
    const cy = s.H * 0.46;
    const st: Stage = { ...s, x: cx, y: cy };
    light(st, cx, cy, 220, '#F07AA8', 0.7, 0.7);
    shock(cx, cy, { col: '#FFFFFF', r: Math.max(s.W, s.H) * 0.45, dur: 1.1, width: 6 });
    burst(st, cx, cy, {
      n: 20,
      speed: [300, 700],
      draw: shard,
      cols: ['#CFF2FF', '#E8F6FF'],
      size: [7, 13],
      life: [1, 1.5],
      gravity: 700,
      drag: 0.6,
      vr: [-10, 10],
    });
    burst(st, cx, cy, {
      n: 60,
      speed: [300, 820],
      draw: SWEETS,
      size: [13, 20],
      life: [1.6, 2.2],
      gravity: 650,
      drag: 0.55,
      vr: [-6, 6],
    });
    burst(st, cx, cy, {
      n: 150,
      speed: [250, 900],
      draw: sprinkle,
      size: [4, 7],
      life: [1.6, 2.2],
      gravity: 600,
      drag: 0.5,
      vr: [-12, 12],
    });
    glints(st, cx, cy, 20, 80, ['#FFFFFF', '#FFE07A']);
  });
}

export const sweetShop: Preset = {
  pal: SWEET,
  light: '#FFFFFF',
  win(s) {
    burst(s, s.x, s.y, {
      n: 34,
      speed: [160, 380],
      angle: UP,
      spread: Math.PI * 1.5,
      draw: sprinkle,
      size: [4, 6],
      life: [0.7, 0.9],
      gravity: 760,
      drag: 0.5,
      vr: [-10, 10],
    });
    burst(s, s.x, s.y, {
      n: 4,
      speed: [180, 300],
      angle: UP,
      spread: 1.4,
      draw: SWEETS,
      size: [9, 12],
      life: [0.8, 0.95],
      gravity: 760,
      drag: 0.6,
      vr: [-5, 5],
    });
    glints(s, s.x, s.y, 6, 30, ['#FFFFFF', '#FFE07A']);
  },
  big(s) {
    rain(s, {
      n: 220,
      over: 1300,
      vy: [200, 340],
      vx: [-30, 30],
      draw: sprinkle,
      size: [4, 7],
      life: [2, 2.4],
      gravity: 200,
      vr: [-8, 8],
    });
    rain(s, {
      n: 28,
      over: 1100,
      vy: [140, 240],
      draw: SWEETS,
      size: [14, 21],
      life: [2, 2.4],
      gravity: 320,
      vr: [-3, 3],
    });
    burst(s, s.x, s.y, {
      n: 50,
      speed: [220, 520],
      angle: UP,
      spread: Math.PI * 1.4,
      draw: sprinkle,
      size: [3, 5],
      life: [1, 1.3],
      gravity: 700,
      drag: 0.5,
      vr: [-10, 10],
    });
    light(s, s.x, s.y, 100, '#F07AA8', 0.7, 0.5);
  },
  mega(s) {
    rain(s, {
      n: 140,
      over: 1000,
      vy: [200, 340],
      draw: sprinkle,
      size: [4, 7],
      life: [2, 2.4],
      gravity: 200,
      vr: [-8, 8],
    });
    candyJar(s);
    after(1500, () =>
      rain(s, {
        n: 24,
        over: 1000,
        vy: [120, 220],
        draw: SWEETS,
        size: [14, 21],
        life: [2, 2.4],
        gravity: 320,
        vr: [-3, 3],
      }),
    );
  },
};

/* ===================================== Workshop: welding sparks, gears, steam, nut fireworks ===================================== */

const HOT = ['#FFF3D6', '#FFD166', '#FF8A3D'] as const;
const METAL = ['#AAB6CC', '#C9D2E0', '#C9773A', '#8A97AD'] as const;
const PARTS = [gear, nut] as const;

function sparkFountain(s: Stage, x: number, angle: number, n: number, over: number): void {
  burst(s, x, s.H + 4, {
    n,
    over,
    speed: [760, 1120],
    angle,
    spread: 0.4,
    draw: spark,
    cols: HOT,
    size: [1.8, 3],
    life: [0.9, 1.3],
    gravity: 900,
    drag: 0.8,
    face: true,
    add: true,
  });
}

export const workshop: Preset = {
  pal: [...HOT, '#2EE6C9'],
  light: '#FFF3D6',
  win(s) {
    light(s, s.x, s.y, 50, '#FFD166', 0.35, 0.8);
    burst(s, s.x, s.y, {
      n: 48,
      speed: [200, 540],
      angle: UP,
      spread: Math.PI * 1.5,
      draw: spark,
      cols: HOT,
      size: [1.2, 2.2],
      life: [0.4, 0.8],
      gravity: 950,
      drag: 0.6,
      face: true,
      add: true,
    });
    burst(s, s.x, s.y, {
      n: 3,
      speed: [120, 240],
      angle: UP,
      spread: 1.6,
      draw: PARTS,
      cols: METAL,
      col2: '#E4EAF4',
      size: [9, 13],
      life: [0.8, 0.95],
      gravity: 650,
      drag: 0.7,
      vr: [-7, 7],
    });
  },
  big(s) {
    sparkFountain(s, s.W * 0.06, UP + 0.45, 120, 1300);
    sparkFountain(s, s.W * 0.94, UP - 0.45, 120, 1300);
    rain(s, {
      n: 30,
      over: 1100,
      vy: [120, 220],
      vx: [-40, 40],
      draw: PARTS,
      cols: METAL,
      col2: '#E4EAF4',
      size: [12, 19],
      life: [2, 2.4],
      gravity: 480,
      vr: [-5, 5],
    });
    burst(s, s.x, s.y, {
      n: 60,
      speed: [220, 620],
      angle: UP,
      spread: Math.PI * 1.5,
      draw: spark,
      cols: HOT,
      size: [1.4, 2.4],
      life: [0.6, 1],
      gravity: 900,
      drag: 0.6,
      face: true,
      add: true,
    });
    light(s, s.x, s.y, 90, '#FFD166', 0.5, 0.8);
  },
  mega(s) {
    // steam rolls up from the floor, then the nut fireworks go off
    for (let i = 0; i < budget(22); i++)
      spawn({
        x: (s.W * (i + 0.5)) / 22 + between(-30, 30),
        y: s.H + 30,
        vx: between(-20, 20),
        vy: -between(60, 140),
        delay: rand() * 0.6,
        draw: puff,
        col: i % 2 ? '#C9D2E0' : '#E4EAF4',
        size: between(40, 70),
        grow: 1.1,
        life: between(1.8, 2.4),
        alpha: 0.32,
        gravity: -30,
        fadeIn: 0.2,
        fadeOut: 0.5,
      });
    sparkFountain(s, s.W * 0.06, UP + 0.45, 90, 1000);
    sparkFountain(s, s.W * 0.94, UP - 0.45, 90, 1000);
    const cols = ['#FFD166', '#FF8A3D', '#2EE6C9', '#FFF3D6', '#FF8A3D', '#FFD166'];
    for (let i = 0; i < 6; i++) {
      const x = s.W * (0.15 + 0.7 * ((i * 0.618) % 1));
      const y = s.H * between(0.18, 0.4);
      launch(
        s,
        x + between(-40, 40),
        s.H + 10,
        x,
        y,
        cols[i] ?? '#FFD166',
        (ex, ey) =>
          explode(s, ex, ey, {
            col: cols[i] ?? '#FFD166',
            n: 80,
            speed: 400,
            pieces: { n: 14, draw: PARTS, cols: METAL, col2: '#E4EAF4', size: [9, 14] },
          }),
        500 + i * 330,
      );
    }
  },
};

/* ===================================== Koi Pond: lotus petals, ripples, koi in a circle, the golden koi-dragon ===================================== */

const LOTUS = ['#F59AB8', '#FBC4D6', '#FFE3EC', '#F07AA8'] as const;
const KOI_COLS: readonly [string, string][] = [
  ['#F26B3A', '#FFF6EC'],
  ['#FFF6EC', '#E23B2E'],
  ['#FFC94D', '#F26B3A'],
  ['#F26B3A', '#1E2A3F'],
];

function ripples(x: number, y: number, r: number, n = 3, delay = 0): void {
  for (let i = 0; i < n; i++)
    shock(x, y, { col: '#BFEFFF', r, dur: 0.9, width: 2.5, squash: 0.42, delay: delay + i * 0.16 });
}

function koiCircle(n: number): void {
  addActor({
    dur: 2.4,
    draw(g, k, f) {
      const cx = f.W / 2;
      const cy = f.H * 0.48;
      const R = Math.min(f.W, f.H) * 0.3;
      const a = ease.env(k, 0.15, 0.75);
      for (let i = 0; i < n; i++) {
        const an = (i / n) * TAU + f.t * 1.3;
        const [c1, c2] = KOI_COLS[i % KOI_COLS.length] ?? ['#F26B3A', '#FFF6EC'];
        g.globalAlpha = a;
        stamp(
          g,
          koi,
          cx + Math.cos(an) * R,
          cy + Math.sin(an) * R * 0.62,
          Math.min(f.W, f.H) * 0.045,
          an + Math.PI / 2,
          c1,
          c2,
          f.dpr,
          { t: f.t, ph: i },
        );
      }
    },
  });
}

export const koiPond: Preset = {
  pal: LOTUS,
  light: '#FFF6EC',
  win(s) {
    ripples(s.x, s.y, 70);
    burst(s, s.x, s.y, {
      n: 18,
      speed: [100, 240],
      angle: UP,
      spread: Math.PI * 1.5,
      draw: lotus,
      col2: '#FFFFFF',
      size: [5, 8],
      life: [0.7, 0.95],
      gravity: 320,
      drag: 0.5,
      flip: [4, 8],
      vr: [-3, 3],
    });
    glints(s, s.x, s.y, 6, 26, ['#FFFFFF', '#FFC94D']);
  },
  big(s) {
    rain(s, {
      n: 170,
      over: 1400,
      vy: [60, 130],
      vx: [-10, 30],
      draw: lotus,
      col2: '#FFFFFF',
      size: [7, 12],
      life: [2.2, 2.6],
      sway: [20, 50],
      swayF: [2, 3.5],
      flip: [2, 5],
      vr: [-1.5, 1.5],
      gravity: 25,
    });
    koiCircle(6);
    for (let i = 0; i < 4; i++) ripples(s.W * between(0.15, 0.85), s.H * between(0.3, 0.8), 80, 2, i * 0.35);
    ripples(s.x, s.y, 100);
  },
  mega(s) {
    rain(s, {
      n: 150,
      over: 1800,
      vy: [60, 130],
      draw: lotus,
      col2: '#FFFFFF',
      size: [7, 12],
      life: [2.4, 2.8],
      sway: [20, 50],
      swayF: [2, 3.5],
      flip: [2, 5],
      vr: [-1.5, 1.5],
      gravity: 25,
    });
    ripples(s.W / 2, s.H * 0.5, Math.min(s.W, s.H) * 0.45, 4);
    serpent({
      dur: 3.5,
      body: ['#FFD166', '#F2A81D'],
      aura: '#FFC94D',
      width: Math.min(s.W, s.H) * 0.028,
      segments: 34,
      trail: '#FFD166',
      dragon: true,
      path: (k, W, H) => {
        const R = Math.min(W, H) * (0.34 - 0.14 * Math.min(1, k / 0.72));
        if (k < 0.72) {
          const an = Math.PI + (k / 0.72) * TAU * 1.15;
          return { x: W / 2 + Math.cos(an) * R * 1.3, y: H * 0.5 + Math.sin(an) * R };
        }
        // the leap: up through the top
        const q = (k - 0.72) / 0.28;
        const an = Math.PI + TAU * 1.15;
        const x0 = W / 2 + Math.cos(an) * R * 1.3;
        const y0 = H * 0.5 + Math.sin(an) * R;
        return { x: x0 + q * W * 0.12, y: y0 - q * q * H * 0.9 };
      },
    });
    wash(s.W / 2, s.H * 0.5, { col: '#FFC94D', r: Math.max(s.W, s.H) * 0.55, dur: 3.2, alpha: 0.3 });
  },
};
